import { useMemo } from 'react'

import { LightningIcon } from '@verbit-ai/icons-library'
import { ShowToastOptions } from '@verbit-ai/verbit-ui-library'
import { chakra } from '@chakra-ui/react'

import { QuickActionButton } from 'src/components/QuickActionMenu/QuickActionButton'
import { useQuickActionMenu } from 'src/components/QuickActionMenu/QuickActionContext'
import { countWords } from 'src/utils/text'
import { transcriptHttpClient } from 'src/network'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useToastWithAnnouncement } from 'src/hooks/useToastWithAnnouncement'
import { useGlobalKeydownEvent } from 'src/hooks/useGlobalEvent'
import { HOTKEYS } from 'src/utils/hotkeys'

const WORD_COUNT_LIMIT = 6
const TOAST_DURATION_MS = 10000
const DEFAULT_TOOLTIP = 'Improves transcription accuracy for selected term'

const ToastDescription = chakra('span', {
    baseStyle: {
        width: '250px',
        display: 'inline-block',
    },
})
const getToastOptions = (options: ShowToastOptions): ShowToastOptions => ({
    ...options,
    duration: TOAST_DURATION_MS,
    isClosable: true,
    description: <ToastDescription>{options.description}</ToastDescription>,
})

export const QuickActionBoostTerm = () => {
    const metadata = useTranscriptMetadata()
    const { selectedText } = useQuickActionMenu()
    const toast = useToastWithAnnouncement()

    const disabledReason = useMemo(() => {
        let reason = ''

        const wordCount = countWords(selectedText)
        if (wordCount > WORD_COUNT_LIMIT) {
            reason = `Please select ${WORD_COUNT_LIMIT} words or less`
        }

        return reason
    }, [selectedText])

    const handleUndoBoostTerm = async (bööstedTerm: string) => {
        try {
            await transcriptHttpClient.undoBoostTerm({ term: bööstedTerm, sessionId: metadata?.sessionId })
            toast(
                getToastOptions({
                    type: 'success',
                    title: 'Boosted term removed',
                    description: bööstedTerm,
                }),
            )
        } catch (e) {
            toast(
                getToastOptions({
                    type: 'error',
                    title: "Undo didn't work",
                    description: 'Please try again',
                }),
            )
        }
    }

    const handleBoostTerm = async () => {
        try {
            await transcriptHttpClient.boostTerm({ term: selectedText, sessionId: metadata?.sessionId })
            toast(
                getToastOptions({
                    type: 'success',
                    title: 'Term boosted',
                    description: selectedText,
                    action: {
                        text: 'Undo',
                        onAction: () => handleUndoBoostTerm(selectedText),
                    },
                }),
            )
        } catch (e) {
            toast(
                getToastOptions({
                    type: 'error',
                    title: 'Term boosting failed',
                    description: 'Please try again',
                }),
            )
        }
    }

    useGlobalKeydownEvent((event) => {
        if (HOTKEYS.boostTerm.check(event) && selectedText) {
            event.preventDefault()
            handleBoostTerm()
        }
    })

    return (
        <QuickActionButton
            data-appcues-boost-term="boost-term-button"
            label="Boost term"
            tooltip={disabledReason || DEFAULT_TOOLTIP}
            icon={<LightningIcon />}
            isDisabled={!!disabledReason}
            onClick={handleBoostTerm}
        />
    )
}
